import React, { useCallback, useEffect, useState, useRef, Fragment, Component } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Plus, X, Loader2, Sparkles, Users, Filter, ChevronDown, Trash2, Eye, Search, Target, Zap, Hash, Type, ToggleLeft, List, AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { crmContactsApi, CrmContact } from '../services/crmApi';
import { audienceStorage, discoverPayloadFields, filterContactsByRules, STANDARD_FIELDS, discoverCrmFieldsFromContacts, OPERATOR_LABELS, getOperatorsForFieldType, generateId, createEmptyRule, DiscoveredField, AudienceRule, SavedAudience, StandardFieldDef } from '../services/audienceApi';
import { toast } from 'sonner';
// ── Field type icons ──
function FieldTypeIcon({
  type,
  className
}: {
  type: string;
  className?: string;
}) {
  const cls = className || 'h-3.5 w-3.5';
  switch (type) {
    case 'boolean':
      return <ToggleLeft className={cls} data-id="element-1191" />;
    case 'select':
      return <List className={cls} data-id="element-1192" />;
    case 'number':
      return <Hash className={cls} data-id="element-1193" />;
    default:
      return <Type className={cls} data-id="element-1194" />;
  }
}
// ── Contact Preview Panel ──
function ContactPreviewPanel({
  contacts,
  onClose
}: {
  contacts: CrmContact[];
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(100);
  const filtered = search.trim() ? contacts.filter(c => {
    const q = search.toLowerCase();
    const name = (c.Full_Name || `${c.First_Name || ''} ${c.Last_Name || ''}`).toLowerCase();
    const email = (c.Email || '').toLowerCase();
    return name.includes(q) || email.includes(q);
  }) : contacts;
  const visible = filtered.slice(0, visibleCount);
  return <div className="fixed inset-0 z-50 overflow-hidden" data-id="element-1195">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} data-id="element-1196" />
      <div className="absolute inset-y-0 right-0 w-full max-w-2xl bg-white shadow-xl flex flex-col" data-id="element-1197">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-1198">
          <div data-id="element-1199">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-1200">
              Matching Contacts
            </h2>
            <p className="text-sm text-gray-500 mt-0.5" data-id="element-1201">
              {contacts.length} contact{contacts.length !== 1 ? 's' : ''} match
              your rules
              {search.trim() && filtered.length !== contacts.length && <span data-id="element-1202"> · {filtered.length} matching search</span>}
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100" data-id="element-1203">
            <X className="h-5 w-5" data-id="element-1204" />
          </button>
        </div>

        <div className="px-6 py-3 border-b border-gray-100" data-id="element-1205">
          <div className="relative" data-id="element-1206">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-1207" />
            <input type="text" value={search} onChange={e => {
            setSearch(e.target.value);
            setVisibleCount(100);
          }} placeholder="Search by name or email..." className="block w-full pl-9 pr-4 py-2 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" data-id="element-1208" />
          </div>
        </div>

        <div className="flex-1 overflow-auto" data-id="element-1209">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-1210">
            <thead className="bg-gray-50 sticky top-0" data-id="element-1211">
              <tr data-id="element-1212">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1213">
                  #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1214">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1215">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1216">
                  Region
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-1217">
              {visible.map((contact, index) => <tr key={contact.id || index} data-id="element-1218">
                  <td className="px-6 py-3 whitespace-nowrap text-xs text-gray-400" data-id="element-1219">
                    {index + 1}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900" data-id="element-1220">
                    {contact.Full_Name || `${contact.First_Name || ''} ${contact.Last_Name || ''}`.trim() || <span className="text-gray-400" data-id="element-1221">—</span>}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600" data-id="element-1222">
                    {contact.Email || <span className="text-gray-400" data-id="element-1223">—</span>}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600" data-id="element-1224">
                    {contact.Sub_Region || contact.Region || <span className="text-gray-400" data-id="element-1225">—</span>}
                  </td>
                </tr>)}
              {visible.length === 0 && <tr data-id="element-1226">
                  <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500" data-id="element-1227">
                    {search.trim() ? 'No contacts match your search' : 'No matching contacts'}
                  </td>
                </tr>}
            </tbody>
          </table>

          {filtered.length > visibleCount && <div className="px-6 py-4 text-center border-t border-gray-100" data-id="element-1228">
              <button onClick={() => setVisibleCount(prev => prev + 100)} className="text-sm text-blue-600 hover:text-blue-800 font-medium" data-id="element-1229">
                Show more ({filtered.length - visibleCount} remaining)
              </button>
            </div>}
        </div>
      </div>
    </div>;
}
// ── Rule Row Component ──
function RuleRow({
  rule,
  index,
  crmFields,
  discoveredFields,
  onChange,
  onDelete
}: {
  rule: AudienceRule;
  index: number;
  crmFields: StandardFieldDef[];
  discoveredFields: DiscoveredField[];
  onChange: (updates: Partial<AudienceRule>) => void;
  onDelete: () => void;
}) {
  // Determine field type and available operators
  const standardField = crmFields.find(f => f.key === rule.field);
  const payloadField = discoveredFields.find(f => f.key === rule.field);
  const fieldType = standardField?.type || payloadField?.type || 'text';
  const operators = standardField ? standardField.operators : getOperatorsForFieldType(fieldType);
  const needsValueInput = !['is_true', 'is_false', 'is_empty', 'is_not_empty'].includes(rule.operator);
  const isSelectField = fieldType === 'select' && payloadField?.values;
  return <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-1230">
      <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2" data-id="element-1231">
        {/* Field selector */}
        <div data-id="element-1232">
          <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-1233">
            Field
          </label>
          <select value={rule.field ? `${rule.fieldSource}:${rule.field}` : ''} onChange={e => {
          const val = e.target.value;
          if (!val) {
            onChange({
              field: '',
              fieldSource: 'standard',
              operator: 'equals',
              value: ''
            });
            return;
          }
          const [source, ...keyParts] = val.split(':');
          const key = keyParts.join(':');
          const newSource = source as 'standard' | 'payload';
          // Reset operator to first valid one for the new field type
          const sf = crmFields.find(f => f.key === key);
          const pf = discoveredFields.find(f => f.key === key);
          const ft = sf?.type || pf?.type || 'text';
          const ops = sf ? sf.operators : getOperatorsForFieldType(ft);
          const defaultOp = ops[0] || 'equals';
          onChange({
            field: key,
            fieldSource: newSource,
            operator: defaultOp,
            value: ''
          });
        }} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" data-id="element-1234">
            <option value="" data-id="element-1235">Select field...</option>
            <optgroup label="CRM Fields" data-id="element-1236">
              {crmFields.map(f => <option key={f.key} value={`standard:${f.key}`} data-id="element-1237">
                  {f.label}
                </option>)}
            </optgroup>
            {discoveredFields.length > 0 && <optgroup label="Form Data Fields" data-id="element-1238">
                {discoveredFields.map(f => <option key={f.key} value={`payload:${f.key}`} data-id="element-1239">
                    {f.label}
                  </option>)}
              </optgroup>}
          </select>
        </div>

        {/* Operator selector */}
        <div data-id="element-1240">
          <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-1241">
            Condition
          </label>
          <select value={rule.operator} onChange={e => onChange({
          operator: e.target.value as AudienceRule['operator'],
          value: ''
        })} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" disabled={!rule.field} data-id="element-1242">
            {operators.map(op => <option key={op} value={op} data-id="element-1243">
                {OPERATOR_LABELS[op]}
              </option>)}
          </select>
        </div>

        {/* Value input */}
        <div data-id="element-1244">
          <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-1245">
            Value
          </label>
          {!needsValueInput ? <div className="h-[38px] flex items-center px-3 text-sm text-gray-400 italic bg-gray-100 rounded-md border border-gray-200" data-id="element-1246">
              No value needed
            </div> : isSelectField ? rule.operator === 'in' ? <div className="space-y-1" data-id="element-1247">
                {(payloadField?.values || []).map(val => {
            const selected = Array.isArray(rule.value) ? rule.value.includes(val) : rule.value === val;
            return <label key={val} className="flex items-center gap-2 text-sm text-gray-700" data-id="element-1248">
                      <input type="checkbox" checked={selected} onChange={() => {
                const current = Array.isArray(rule.value) ? rule.value : rule.value ? [rule.value] : [];
                const next = selected ? current.filter(v => v !== val) : [...current, val];
                onChange({
                  value: next
                });
              }} className="h-3.5 w-3.5 text-blue-600 rounded border-gray-300" data-id="element-1249" />
                      {val}
                    </label>;
          })}
              </div> : <select value={Array.isArray(rule.value) ? rule.value[0] || '' : rule.value} onChange={e => onChange({
          value: e.target.value
        })} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" data-id="element-1250">
                <option value="" data-id="element-1251">Select value...</option>
                {(payloadField?.values || []).map(val => <option key={val} value={val} data-id="element-1252">
                    {val}
                  </option>)}
              </select> : <input type={fieldType === 'number' ? 'number' : 'text'} value={Array.isArray(rule.value) ? rule.value.join(', ') : rule.value} onChange={e => onChange({
          value: e.target.value
        })} placeholder="Enter value..." className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" disabled={!rule.field} data-id="element-1253" />}
        </div>
      </div>

      <button onClick={onDelete} className="mt-5 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded flex-shrink-0" title="Remove rule" data-id="element-1254">
        <X className="h-4 w-4" data-id="element-1255" />
      </button>
    </div>;
}
// ── Main Editor ──
export function AudienceEditor() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const isEditing = !!id && id !== 'new';
  // Audience state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [rules, setRules] = useState<AudienceRule[]>([]);
  const [logic, setLogic] = useState<'AND' | 'OR'>('AND');
  // Data state
  const [allContacts, setAllContacts] = useState<CrmContact[]>([]);
  const [contactsLoaded, setContactsLoaded] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [loadProgress, setLoadProgress] = useState('');
  // Field discovery state
  const [discoveredFields, setDiscoveredFields] = useState<DiscoveredField[]>([]);
  const [crmFields, setCrmFields] = useState<StandardFieldDef[]>(STANDARD_FIELDS);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [discoveryProgress, setDiscoveryProgress] = useState('');
  const [discoveryDone, setDiscoveryDone] = useState(false);
  // Preview state
  const [matchingContacts, setMatchingContacts] = useState<CrmContact[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Debounce timer ref
  const filterTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // ── Reset all contact data when workspace or sub-region changes ──
  // This prevents stale data from a previous scope being used for filtering.
  const prevScopeRef = useRef<string>('');
  useEffect(() => {
    const scopeKey = `${currentWorkspace.name}::${currentSubRegion || ''}`;
    if (prevScopeRef.current && prevScopeRef.current !== scopeKey) {
      // Scope changed — reset everything
      setAllContacts([]);
      setContactsLoaded(false);
      setMatchingContacts([]);
      setDiscoveredFields([]);
      setDiscoveryDone(false);
      setLoadProgress('');
    }
    prevScopeRef.current = scopeKey;
  }, [currentWorkspace.name, currentSubRegion]);
  // Load existing audience
  useEffect(() => {
    if (isEditing && id) {
      const loadAudience = async () => {
        // Try localStorage first (fast)
        let existing = audienceStorage.get(id);
        // If not in localStorage, try syncing from Creator
        if (!existing) {
          try {
            await audienceStorage.syncFromCreator(currentWorkspace.name);
            existing = audienceStorage.get(id);
          } catch (err) {
            console.warn('Failed to sync audience from Creator:', err);
          }
        }
        if (existing) {
          setName(existing.name);
          setDescription(existing.description);
          setRules(existing.rules);
          setLogic(existing.logic);
        } else {
          toast.error('Audience not found');
          navigate('/audiences');
        }
      };
      loadAudience();
    }
  }, [id, isEditing, navigate, currentWorkspace.name]);
  // Load contacts
  const loadContacts = useCallback(async () => {
    if (contactsLoaded) return allContacts;
    setLoadingContacts(true);
    setLoadProgress('Loading contacts...');
    try {
      const contacts = await crmContactsApi.listAll(count => {
        setLoadProgress(`Loading contacts: ${count} fetched...`);
      });
      // Filter by team: include contacts that match the current workspace
      // or have no team set (they're still eligible)
      let scopedContacts = contacts.filter(c => !c.Team || c.Team === currentWorkspace.name);
      // Filter by sub-region if one is currently in scope
      if (currentSubRegion) {
        scopedContacts = scopedContacts.filter(c => !c.Sub_Region || c.Sub_Region === currentSubRegion);
      }
      setAllContacts(scopedContacts);
      setContactsLoaded(true);
      setLoadProgress('');
      // Discover CRM fields from loaded contacts
      const discovered = discoverCrmFieldsFromContacts(scopedContacts);
      setCrmFields(discovered);
      return scopedContacts;
    } catch (err) {
      console.error('Failed to load contacts:', err);
      toast.error('Failed to load contacts');
      setLoadProgress('');
      return [];
    } finally {
      setLoadingContacts(false);
    }
  }, [contactsLoaded, allContacts, currentWorkspace.name, currentSubRegion]);
  // Discover fields
  const handleDiscoverFields = async () => {
    setIsDiscovering(true);
    setDiscoveryProgress('Loading contacts...');
    try {
      const contacts = await loadContacts();
      // Refresh CRM fields from contacts
      const crmDiscovered = discoverCrmFieldsFromContacts(contacts);
      setCrmFields(crmDiscovered);
      setDiscoveryProgress(`Analyzing form data from ${contacts.length} contacts...`);
      // Collect Form_Payload values
      const payloads: string[] = contacts.map(c => (c as any).Form_Payload).filter(Boolean);
      if (payloads.length === 0) {
        toast.warning('No form payload data found on contacts', {
          description: 'Contacts need to have Form_Payload data from signup form submissions.'
        });
        setDiscoveryDone(true);
        setIsDiscovering(false);
        setDiscoveryProgress('');
        return;
      }
      setDiscoveryProgress(`Found ${payloads.length} contacts with form data. Analyzing with AI...`);
      const fields = await discoverPayloadFields(payloads);
      setDiscoveredFields(fields);
      setDiscoveryDone(true);
      if (fields.length > 0) {
        toast.success(`Discovered ${fields.length} filterable form fields`);
      } else {
        toast.info('No filterable fields found in form data');
      }
    } catch (err) {
      console.error('Field discovery failed:', err);
      toast.error(`Field analysis failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsDiscovering(false);
      setDiscoveryProgress('');
    }
  };
  // Apply filters (debounced)
  useEffect(() => {
    if (filterTimerRef.current) clearTimeout(filterTimerRef.current);
    const validRules = rules.filter(r => r.field);
    if (validRules.length === 0) {
      setMatchingContacts([]);
      return;
    }
    if (!contactsLoaded) return;
    filterTimerRef.current = setTimeout(() => {
      const result = filterContactsByRules(allContacts, validRules, logic);
      setMatchingContacts(result);
    }, 300);
    return () => {
      if (filterTimerRef.current) clearTimeout(filterTimerRef.current);
    };
  }, [rules, logic, allContacts, contactsLoaded]);
  // Rule handlers
  const addRule = () => {
    setRules(prev => [...prev, createEmptyRule()]);
  };
  const updateRule = (id: string, updates: Partial<AudienceRule>) => {
    setRules(prev => prev.map(r => r.id === id ? {
      ...r,
      ...updates
    } : r));
  };
  const deleteRule = (id: string) => {
    setRules(prev => prev.filter(r => r.id !== id));
  };
  // Save
  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Please enter an audience name');
      return;
    }
    const validRules = rules.filter(r => r.field);
    if (validRules.length === 0) {
      toast.error('Please add at least one filter rule');
      return;
    }
    // Check for incomplete rules — operators that require a value but have none
    const valueRequiringOperators = ['equals', 'not_equals', 'contains', 'not_contains', 'in'];
    const incompleteRules = validRules.filter(r => {
      if (!valueRequiringOperators.includes(r.operator)) return false;
      if (Array.isArray(r.value)) return r.value.filter(Boolean).length === 0;
      return !r.value && r.value !== '0';
    });
    if (incompleteRules.length > 0) {
      toast.error(`${incompleteRules.length} rule${incompleteRules.length !== 1 ? 's' : ''} missing a value. Please complete all rules or remove empty ones.`);
      return;
    }
    setIsSaving(true);
    try {
      const audience: SavedAudience = {
        id: isEditing && id ? id : generateId(),
        name: name.trim(),
        description: description.trim(),
        team: currentWorkspace.name,
        region: currentSubRegion || undefined,
        rules: validRules,
        logic,
        createdAt: isEditing ? audienceStorage.get(id!)?.createdAt || new Date().toISOString() : new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        lastCount: matchingContacts.length
      };
      const result = await audienceStorage.save(audience);
      if (result.synced) {
        toast.success(isEditing ? 'Audience updated & saved to Creator' : 'Audience created & saved to Creator');
      } else {
        toast.warning(isEditing ? 'Audience updated locally' : 'Audience created locally', {
          description: `Creator sync failed: ${result.error}. Data is saved locally and will sync on next save.`
        });
      }
      navigate('/audiences');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Failed to save audience: ${msg}`);
    } finally {
      setIsSaving(false);
    }
  };
  const validRuleCount = rules.filter(r => r.field).length;
  return <div className="space-y-6 max-w-4xl mx-auto pb-20" data-id="element-1256">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-1257">
        <div className="flex items-center gap-4" data-id="element-1258">
          <Link to="/audiences" className="p-2 hover:bg-gray-100 rounded-full text-gray-500" data-id="element-1259">
            <ArrowLeft className="h-5 w-5" data-id="element-1260" />
          </Link>
          <div data-id="element-1261">
            <h1 className="text-2xl font-semibold text-gray-900" data-id="element-1262">
              {isEditing ? 'Edit Audience' : 'Create Audience'}
            </h1>
            <p className="text-sm text-gray-500" data-id="element-1263">
              Build a dynamic audience using CRM fields and form submission
              data.
            </p>
          </div>
        </div>
        <button onClick={handleSave} disabled={isSaving || !name.trim() || validRuleCount === 0} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-1264">
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-1265" /> : <Save className="h-4 w-4" data-id="element-1266" />}
          {isSaving ? 'Saving...' : 'Save Audience'}
        </button>
      </div>

      {/* Audience Name */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6" data-id="element-1267">
        <div className="grid grid-cols-1 gap-4" data-id="element-1268">
          <div data-id="element-1269">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1270">
              Audience Name
            </label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Physical Attendees in Brazil" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" data-id="element-1271" />
          </div>
          <div data-id="element-1272">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1273">
              Description{' '}
              <span className="text-gray-400 font-normal" data-id="element-1274">(optional)</span>
            </label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} placeholder="Describe who this audience targets..." className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" data-id="element-1275" />
          </div>
        </div>
      </div>

      {/* Field Discovery */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden" data-id="element-1276">
        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-orange-50" data-id="element-1277">
          <div className="flex items-center justify-between" data-id="element-1278">
            <div className="flex items-center gap-3" data-id="element-1279">
              <div className="p-2 bg-amber-100 rounded-lg" data-id="element-1280">
                <Sparkles className="h-5 w-5 text-amber-600" data-id="element-1281" />
              </div>
              <div data-id="element-1282">
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-1283">
                  AI Field Discovery
                </h2>
                <p className="text-xs text-gray-600 mt-0.5" data-id="element-1284">
                  Analyze form submission data to discover filterable fields
                </p>
              </div>
            </div>
            <button onClick={handleDiscoverFields} disabled={isDiscovering} className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-md hover:bg-amber-700 disabled:opacity-50" data-id="element-1285">
              {isDiscovering ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-1286" /> : discoveryDone ? <RefreshCw className="h-4 w-4" data-id="element-1287" /> : <Zap className="h-4 w-4" data-id="element-1288" />}
              {isDiscovering ? 'Analyzing...' : discoveryDone ? 'Re-analyze' : 'Analyze Form Data'}
            </button>
          </div>

          {/* Progress */}
          {(isDiscovering || loadingContacts) && <div className="mt-3 flex items-center gap-2 text-sm text-amber-700" data-id="element-1289">
              <Loader2 className="h-3.5 w-3.5 animate-spin flex-shrink-0" data-id="element-1290" />
              <span data-id="element-1291">{discoveryProgress || loadProgress}</span>
            </div>}
        </div>

        {/* Discovered fields */}
        {discoveredFields.length > 0 && <div className="px-6 py-4" data-id="element-1292">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3" data-id="element-1293">
              {discoveredFields.length} Discovered Field
              {discoveredFields.length !== 1 ? 's' : ''}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-id="element-1294">
              {discoveredFields.map(field => <div key={field.key} className="flex items-start gap-2.5 p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-1295">
                  <div className="mt-0.5 p-1 bg-white rounded border border-gray-200" data-id="element-1296">
                    <FieldTypeIcon type={field.type} className="h-3.5 w-3.5 text-gray-500" data-id="element-1297" />
                  </div>
                  <div className="min-w-0 flex-1" data-id="element-1298">
                    <p className="text-sm font-medium text-gray-900 truncate" data-id="element-1299">
                      {field.label}
                    </p>
                    <p className="text-[11px] text-gray-500 font-mono" data-id="element-1300">
                      {field.key}
                    </p>
                    {field.type === 'select' && field.values && <div className="flex flex-wrap gap-1 mt-1.5" data-id="element-1301">
                        {field.values.slice(0, 4).map(v => <span key={v} className="inline-block px-1.5 py-0.5 text-[10px] font-medium bg-blue-50 text-blue-700 rounded" data-id="element-1302">
                            {v}
                          </span>)}
                        {field.values.length > 4 && <span className="text-[10px] text-gray-400" data-id="element-1303">
                            +{field.values.length - 4} more
                          </span>}
                      </div>}
                    {field.type === 'boolean' && <span className="inline-block mt-1 px-1.5 py-0.5 text-[10px] font-medium bg-green-50 text-green-700 rounded" data-id="element-1304">
                        Yes / No
                      </span>}
                  </div>
                </div>)}
            </div>
          </div>}

        {discoveryDone && discoveredFields.length === 0 && <div className="px-6 py-4" data-id="element-1305">
            <div className="flex items-center gap-2 text-sm text-gray-500" data-id="element-1306">
              <AlertTriangle className="h-4 w-4 text-amber-500" data-id="element-1307" />
              No filterable form fields found. Contacts may not have
              Form_Payload data yet.
            </div>
          </div>}

        {!discoveryDone && !isDiscovering && <div className="px-6 py-4" data-id="element-1308">
            <p className="text-sm text-gray-500" data-id="element-1309">
              Click <strong data-id="element-1310">Analyze Form Data</strong> to scan your contacts'
              form submissions and discover what fields you can filter on. This
              uses AI to identify checkboxes, dropdowns, text fields, and more
              from the raw form data.
            </p>
          </div>}
      </div>

      {/* Filter Rules */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm" data-id="element-1311">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between" data-id="element-1312">
          <div className="flex items-center gap-2" data-id="element-1313">
            <Filter className="h-4 w-4 text-gray-400" data-id="element-1314" />
            <h2 className="text-sm font-semibold text-gray-900" data-id="element-1315">
              Filter Rules
            </h2>
            {rules.length > 0 && <span className="text-xs text-gray-500" data-id="element-1316">
                ({validRuleCount} active)
              </span>}
          </div>
          <div className="flex items-center gap-3" data-id="element-1317">
            {/* Logic toggle */}
            {rules.length > 1 && <div className="flex items-center gap-1 bg-gray-100 rounded-md p-0.5" data-id="element-1318">
                <button onClick={() => setLogic('AND')} className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${logic === 'AND' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-1319">
                  AND
                </button>
                <button onClick={() => setLogic('OR')} className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${logic === 'OR' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-1320">
                  OR
                </button>
              </div>}
            <button onClick={addRule} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md" data-id="element-1321">
              <Plus className="h-3.5 w-3.5" data-id="element-1322" />
              Add Rule
            </button>
          </div>
        </div>

        <div className="p-6" data-id="element-1323">
          {rules.length === 0 ? <div className="text-center py-8" data-id="element-1324">
              <Filter className="mx-auto h-10 w-10 text-gray-300" data-id="element-1325" />
              <p className="mt-2 text-sm text-gray-500" data-id="element-1326">
                No filter rules yet. Add a rule to start building your audience.
              </p>
              <button onClick={addRule} className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-1327">
                <Plus className="h-4 w-4" data-id="element-1328" />
                Add First Rule
              </button>
            </div> : <div className="space-y-2" data-id="element-1329">
              {rules.map((rule, index) => <Fragment key={rule.id} data-id="element-1330">
                  {index > 0 && <div className="flex items-center justify-center py-1" data-id="element-1331">
                      <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest px-3 py-0.5 bg-gray-100 rounded-full" data-id="element-1332">
                        {logic}
                      </span>
                    </div>}
                  <RuleRow rule={rule} index={index} crmFields={crmFields} discoveredFields={discoveredFields} onChange={updates => updateRule(rule.id, updates)} onDelete={() => deleteRule(rule.id)} data-id="element-1333" />
                </Fragment>)}
            </div>}
        </div>
      </div>

      {/* Live Preview */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6" data-id="element-1334">
        <div className="flex items-center justify-between" data-id="element-1335">
          <div className="flex items-center gap-3" data-id="element-1336">
            <div className={`p-2 rounded-lg ${matchingContacts.length > 0 ? 'bg-green-50' : 'bg-gray-100'}`} data-id="element-1337">
              <Users className={`h-5 w-5 ${matchingContacts.length > 0 ? 'text-green-600' : 'text-gray-400'}`} data-id="element-1338" />
            </div>
            <div data-id="element-1339">
              <h2 className="text-sm font-semibold text-gray-900" data-id="element-1340">
                Audience Preview
              </h2>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-1341">
                {!contactsLoaded ? 'Load contacts to see preview' : validRuleCount === 0 ? 'Add filter rules to see matching contacts' : <>
                    <span className="font-semibold text-gray-900" data-id="element-1342">
                      {matchingContacts.length}
                    </span>{' '}
                    contact{matchingContacts.length !== 1 ? 's' : ''} match{' '}
                    {validRuleCount} rule{validRuleCount !== 1 ? 's' : ''} (
                    {logic} logic)
                  </>}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2" data-id="element-1343">
            {!contactsLoaded && <button onClick={loadContacts} disabled={loadingContacts} className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 disabled:opacity-50" data-id="element-1344">
                {loadingContacts ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-1345" /> : <RefreshCw className="h-4 w-4" data-id="element-1346" />}
                {loadingContacts ? 'Loading...' : 'Load Contacts'}
              </button>}
            {matchingContacts.length > 0 && <button onClick={() => setShowPreview(true)} className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-1347">
                <Eye className="h-4 w-4" data-id="element-1348" />
                View Contacts
              </button>}
          </div>
        </div>

        {loadingContacts && <div className="mt-3 flex items-center gap-2 text-sm text-gray-500" data-id="element-1349">
            <Loader2 className="h-3.5 w-3.5 animate-spin" data-id="element-1350" />
            {loadProgress}
          </div>}

        {/* Quick stats when we have matches */}
        {matchingContacts.length > 0 && <div className="mt-4 grid grid-cols-3 gap-4" data-id="element-1351">
            <div className="bg-gray-50 rounded-lg p-3 text-center" data-id="element-1352">
              <p className="text-2xl font-bold text-gray-900" data-id="element-1353">
                {matchingContacts.length}
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-1354">Total Matches</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center" data-id="element-1355">
              <p className="text-2xl font-bold text-gray-900" data-id="element-1356">
                {matchingContacts.filter(c => c.Email).length}
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-1357">With Email</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center" data-id="element-1358">
              <p className="text-2xl font-bold text-gray-900" data-id="element-1359">
                {new Set(matchingContacts.map(c => c.Sub_Region || c.Region).filter(Boolean)).size}
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-1360">Regions</p>
            </div>
          </div>}
      </div>

      {/* Contact Preview Panel */}
      {showPreview && <ContactPreviewPanel contacts={matchingContacts} onClose={() => setShowPreview(false)} data-id="element-1361" />}
    </div>;
}